/**
 * Enterprise-grade i18n Configuration
 * 
 * Features:
 * - Lazy loading for optimal performance
 * - Namespace-based translations for scalability
 * - Automatic RTL/LTR switching
 * - Persistent language detection
 * - SSR compatibility
 * - Hot module replacement support (dev)
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Language Configuration
export const LANGUAGES = {
    en: { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', dir: 'ltr' },
    ar: { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇮🇶', dir: 'rtl' },
    ku: { code: 'ku', name: 'Kurdish', nativeName: 'کوردی', flag: '🇮🇶', dir: 'rtl' }
};

export const DEFAULT_LANGUAGE = 'en';
export const RTL_LANGUAGES = ['ar', 'ku'];

// Namespaces for better organization and lazy loading
export const NAMESPACES = [
    'common',      // Common UI elements (buttons, labels)
    'notification',// Notifications
    'auth',        // Authentication pages
    'dashboard',   // Dashboard
    'patient',     // Patient management
    'dentist',     // Dentist management
    'appointment', // Appointments
    'treatment',   // Treatments
    'tooth',       // Dental chart
    'prescription',// Prescriptions
    'payment',     // Payments & Invoices
    'clinic',      // Clinic settings
    'validation',  // Form validation messages
    'messages',    // System messages
    'reports',     // Reports
    'superadmin',  // Super admin dashboard
    'guide',       // System Guide
    'expense',     // Expense management
    'laboratory',  // Laboratory management
    'radiology',   // Radiology management
    'pharmacy'     // Pharmacy management
];

/**
 * Check if a language is RTL
 * @param {string} lng - Language code
 * @returns {boolean}
 */
export const isRTL = (lng) => RTL_LANGUAGES.includes(lng);

/**
 * Get language direction
 * @param {string} lng - Language code
 * @returns {string} - 'rtl' or 'ltr'
 */
export const getDirection = (lng) => isRTL(lng) ? 'rtl' : 'ltr';

/**
 * Apply direction to document
 * @param {string} lng - Language code
 */
const applyDirection = (lng) => {
    const dir = getDirection(lng);
    const htmlElement = document.documentElement;
    const bodyElement = document.body;

    // Set dir and lang attributes
    htmlElement.dir = dir;
    htmlElement.lang = lng;

    // Update body classes for styling hooks
    bodyElement.classList.remove('rtl', 'ltr');
    bodyElement.classList.add(dir);

    // Store direction in CSS custom property for dynamic styles
    htmlElement.style.setProperty('--text-direction', dir);
};

// Initialize i18next with lazy loading
i18n
    .use(Backend) // Enable lazy loading
    .use(LanguageDetector) // Detect user language
    .use(initReactI18next) // Bind to React
    .init({
        // Backend configuration for lazy loading
        backend: {
            loadPath: '/locales/{{lng}}/{{ns}}.json',
            addPath: '/locales/{{lng}}/{{ns}}.json',
            // Allow cross origin requests (for development)
            crossDomain: false,
            // Parse data after it has been fetched
            parse: (data) => JSON.parse(data),
        },

        // Fallback configuration
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs: Object.keys(LANGUAGES),

        // Namespace configuration
        ns: NAMESPACES,
        defaultNS: 'common',
        fallbackNS: 'common',

        // Language detection configuration
        detection: {
            // Order of detection methods
            order: ['localStorage', 'sessionStorage', 'navigator', 'htmlTag'],
            // Cache user language on
            caches: ['localStorage', 'sessionStorage'],
            // Keys for localStorage/sessionStorage
            lookupLocalStorage: 'i18nextLng',
            lookupSessionStorage: 'i18nextLng',
            // Exclude certain domains
            excludeCacheFor: ['cimode'],
            // Check all fallbackLng
            checkWhitelist: true,
        },

        // Interpolation configuration
        interpolation: {
            escapeValue: false, // React already escapes
            formatSeparator: ',',
            // Format values
            format: (value, format, lng) => {
                if (format === 'uppercase') return value.toUpperCase();
                if (format === 'lowercase') return value.toLowerCase();
                if (value instanceof Date) return new Intl.DateTimeFormat(lng).format(value);
                return value;
            },
        },

        // React specific configuration
        react: {
            // Use Suspense for async loading
            useSuspense: true,
            // Bind i18n store to trigger rerenders
            bindI18n: 'languageChanged loaded',
            bindI18nStore: 'added removed',
            // Wait for all namespaces to load
            nsMode: 'default',
        },

        // Key separator - enable dot notation for nested keys
        keySeparator: '.',
        // Namespace separator
        nsSeparator: ':',

        // Return objects
        returnObjects: false,
        returnEmptyString: false,
        returnNull: false,

        // Performance optimizations
        load: 'languageOnly', // Load only 'en', not 'en-US'
        preload: [DEFAULT_LANGUAGE], // Preload default language
        cleanCode: true, // Clean language code (en-US -> en)
        lowerCaseLng: true, // Use lowercase language codes

        // Development settings
        debug: process.env.NODE_ENV === 'development',
        saveMissing: process.env.NODE_ENV === 'development',
        missingKeyHandler: (lngs, ns, key) => {
            if (process.env.NODE_ENV === 'development') {
                console.warn(`Missing translation key: ${key} in ${ns} for ${lngs.join(', ')}`);
            }
        },
    });

// Apply initial direction
applyDirection(i18n.language || DEFAULT_LANGUAGE);

// Listen for language changes
i18n.on('languageChanged', (lng) => {
    applyDirection(lng);

    // Store language preference using the key i18next looks for
    localStorage.setItem('i18nextLng', lng);

    // Emit custom event for components that need to react
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: lng } }));
});

// Handle initialization
i18n.on('initialized', (options) => {
    if (process.env.NODE_ENV === 'development') {
        console.log('i18n initialized with language:', i18n.language);
    }
});

// Handle failed loading
i18n.on('failedLoading', (lng, ns, msg) => {
    console.error(`Failed loading ${ns} for ${lng}:`, msg);
});

export default i18n;
